import { AUTH_URL } from './apiConfig';
import { fetchJsonWithAuth } from './authApi';
import { getSearchSources } from './sourceProviderActions';

// Bridges ListView's fixed fetch signature onto a single search endpoint, so the
// results table, tabs, paging and sorting are all owned by ListView rather than
// re-implemented in the search form.
//
// ListView calls:
//   fetchData(moduleName, limit, offset, { scope, sort, sortDir, search, filters, queryParams })
//
// and hands whatever comes back to mapListResponse, which resolves rows/total/
// tabs/fields/actions from config (src/config/responseMappings/default.json).
// So this returns the RAW payload — no reshaping — and the response frame in
// Be_Auth_DevOps/docs/candidate-search-api-spec.md is what makes that work.

// The four parameters the API decides everything from. See the spec's §1.
//   module      → which config answers, i.e. the columns and actions returned
//   sourceType  → every ticked source; drives the tab set and the counts
//   scope       → the active tab; whose rows come back. Omitted on first load,
//                 so the API falls back to the first source (Internal)
//   searchMode  → the Boolean toggle; whether text fields are parsed as
//                 expressions or matched literally
export function buildSearchRequest({
  moduleName,
  criteria = {},
  searchMode = 'traditional',
  sources = [],
  sourceModules,
  scope = '',
  sort = '',
  sortDir = '',
}) {
  const activeScope = String(scope ?? '').trim().toLowerCase();
  const sourceList = getSearchSources(sources);

  return {
    ...criteria,
    module: (activeScope && sourceModules?.[activeScope]) || moduleName,
    searchMode,
    ...(sourceList.length ? { sourceType: sourceList.join(',') } : {}),
    // Absent on the first search — the API defaults to the first source rather
    // than the client guessing which tab is "default".
    ...(activeScope ? { scope: activeScope } : {}),
    ...(sort ? { sortBy: sort } : {}),
    ...(sortDir ? { sortOrder: sortDir } : {}),
  };
}

// Tabs normally arrive in the response. This is the fallback for a server that
// only reports per-source counts: build the strip from the CONFIGURED source
// order, never from the response's own `sources` array — the API sorts that
// alphabetically (sort.Strings in models/boolean_search.go), which is what used
// to put Dice ahead of Internal.
export function tabsFromCounts(sources, count) {
  return getSearchSources(sources).map((source) => {
    const value = Number(count?.[source]);
    return {
      key: source,
      label: source.charAt(0).toUpperCase() + source.slice(1),
      ...(Number.isFinite(value) ? { count: value } : {}),
    };
  });
}

// Dice rows arrive in the provider's own shape. Normalizing here — before
// mapListResponse sees them — keeps every consumer downstream working with one
// record shape.
async function normalizeRows(payload) {
  const rows = payload?.data ?? payload?.items;
  if (!Array.isArray(rows) || rows.length === 0) return payload;

  const { isDiceResponse, mapDiceCandidatesToInternal } = await import('./diceCandidateMapper');
  if (!isDiceResponse({ data: payload })) return payload;

  const mapped = mapDiceCandidatesToInternal(rows);
  return { ...payload, data: mapped, items: mapped };
}

export function makeSearchFetcher({
  endpoint,
  criteria,
  searchMode,
  sources,
  sourceModules,
}) {
  if (!endpoint) throw new Error('No search endpoint is configured for this form.');

  return async function fetchSearchResults(moduleName, limit, offset, options = {}) {
    const { scope, sort, sortDir } = options;

    const body = buildSearchRequest({
      moduleName, criteria, searchMode, sources, sourceModules, scope, sort, sortDir,
    });

    let json;
    try {
      json = await fetchJsonWithAuth(AUTH_URL, endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, limit, offset }),
      });
    } catch (error) {
      // A 400 carries { status:false, message, error:{ code, details } }. Lift
      // code/details so the caller can route INVALID_BOOLEAN_QUERY back onto the
      // field that carries the bad expression.
      const envelope = error?.response?.error;
      if (envelope) {
        error.code = envelope.code;
        error.details = envelope.details;
        if (error.response?.message) error.message = error.response.message;
      }
      throw error;
    }

    if (json?.status === false) {
      const error = new Error(json?.message || 'Search failed');
      error.code = json?.error?.code;
      error.details = json?.error?.details;
      throw error;
    }

    const payload = await normalizeRows(json?.data ?? json ?? {});

    // Everything else passes through untouched for mapListResponse.
    return {
      ...payload,
      tabs: payload.tabs?.length ? payload.tabs : tabsFromCounts(sources, payload.count),
    };
  };
}
